import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { FileImageFilled, RobotOutlined, StarTwoTone } from "@ant-design/icons";
import { Col, Image, Popover, Row, Tag } from "antd";
import { useEffect, useMemo, useState } from "react";
import { EMachineTranslationStatus } from "@devowl-wp/api";
import { DEFAULT_LANGUAGE, ERcbTier, ETemplateStatus, TemplateMetaDataField, getAllowedLanguages, getSupportedLanguages } from "@devowl-wp/api-real-cookie-banner";
import { useTranslation } from "../../../../../others/i18n.js";
import { useStores } from "../../../../../store/root.js";
import { colors } from "../../../../../styles/colors.js";
import { service } from "../../../../../styles/service.js";
import { spacing } from "../../../../../styles/spacing.js";
import { compareStrings } from "../../../../../util/string.js";
import { getSupportedLanguageOptions } from "../../../../../util/template/translation.js";
import { getLanguageIcon } from "../../../../../util/translation.js";
import { TemplateFavouriteToggle } from "../fields/favourite/favourite-toggle.js";
import { TemplateMetaDataStatusTag } from "../fields/status.js";
import { TranslationStatus } from "../fields/translation/status/translation-status.js";
/**
 * Hook to generate table columns for template meta data.
 *
 * @param actions - Actions that can be performed on the table.
 * @returns - The columns configuration for the table.
 */ function useTemplateMetaDataColumns(actions = {}) {
    const { rcbAuthenticationStore } = useStores();
    const { t } = useTranslation();
    const languages = useMemo(()=>{
        const supported = getSupportedLanguages(false);
        let userLanguages;
        if (rcbAuthenticationStore.session?.user) {
            const allowed = getAllowedLanguages(rcbAuthenticationStore.session.user);
            userLanguages = allowed ? allowed.map((isoLang)=>isoLang.toString()) : undefined;
        }
        return supported.filter((lang)=>userLanguages ? userLanguages.includes(lang) || lang === DEFAULT_LANGUAGE : true);
    }, [
        rcbAuthenticationStore.session?.user
    ]);
    const [languageGridSize, setLanguageGridSize] = useState(8);
    useEffect(()=>{
        const halfCount = Math.ceil(languages.length / 2);
        setLanguageGridSize(languages.length <= 8 ? 8 : halfCount);
    }, [
        languages
    ]);
    const languageGridStyle = useMemo(()=>({
            display: "grid",
            gridTemplateColumns: `repeat(${languageGridSize}, 18px)`,
            gridColumnGap: "2px",
            gridRowGap: "2px"
        }), [
        languageGridSize
    ]);
    const columns = [
        {
            title: t("Logo"),
            dataIndex: TemplateMetaDataField.Logo,
            key: "logo.key",
            render: (_, record)=>{
                return /*#__PURE__*/ _jsx(Image, {
                    width: "36px",
                    src: record.logo?.downloadUrl,
                    placeholder: /*#__PURE__*/ _jsx(FileImageFilled, {}),
                    preview: false,
                    style: {
                        borderRadius: "0.2rem"
                    }
                });
            },
            align: "center",
            width: "4rem"
        },
        {
            title: t("Headline"),
            dataIndex: TemplateMetaDataField.Headline,
            key: TemplateMetaDataField.Headline,
            defaultSortOrder: "ascend",
            sorter: (a, b)=>compareStrings(a.headline, b.headline),
            render: (_, record)=>{
                return /*#__PURE__*/ _jsx("b", {
                    children: record.headline
                });
            }
        },
        {
            title: t("Subheadline"),
            dataIndex: TemplateMetaDataField.SubHeadline,
            key: TemplateMetaDataField.SubHeadline,
            sorter: (a, b)=>compareStrings(a.subHeadline, b.subHeadline)
        },
        {
            title: t("Extends"),
            dataIndex: TemplateMetaDataField.Extends,
            key: TemplateMetaDataField.Extends,
            sorter: (a, b)=>compareStrings(a.extends?.headline, b.extends?.headline),
            render: (_, record)=>{
                return /*#__PURE__*/ _jsx(_Fragment, {
                    children: record.extends ? /*#__PURE__*/ _jsxs(Tag, {
                        color: "grey",
                        children: [
                            /*#__PURE__*/ _jsx("b", {
                                children: record.extends.headline
                            }),
                            "\xa0",
                            record.extends.subHeadline
                        ]
                    }, `${record.id}-extends`) : /*#__PURE__*/ _jsx(Tag, {
                        color: "lightgrey",
                        children: t("None")
                    }, `${record.id}-extends`)
                });
            }
        },
        {
            title: t("Translations"),
            dataIndex: TemplateMetaDataField.TranslationInfo,
            key: TemplateMetaDataField.TranslationInfo,
            render: (_, record)=>{
                return /*#__PURE__*/ _jsx("div", {
                    style: languageGridStyle,
                    children: languages.map((lang)=>{
                        const foundInfo = record.translationInfo?.find((info)=>info.language === lang);
                        let isComplete = lang === DEFAULT_LANGUAGE;
                        if (!isComplete && foundInfo) {
                            if (Object.hasOwn(foundInfo, "isTranslationCompleteRecursive")) {
                                isComplete = foundInfo.isTranslationCompleteRecursive;
                            } else {
                                isComplete = foundInfo.isTranslationComplete;
                            }
                        }
                        const hasAnyTranslation = foundInfo?.translatedOptionalFields?.length + foundInfo?.translatedRequiredFields?.length > 0;
                        const machineTranslationStatus = foundInfo?.machineTranslationStatus;
                        const defaultLangStatus = record.translationInfo?.find((info)=>info.language === DEFAULT_LANGUAGE);
                        return /*#__PURE__*/ _jsx("div", {
                            style: {
                                lineHeight: 1
                            },
                            children: /*#__PURE__*/ _jsx(Popover, {
                                title: /*#__PURE__*/ _jsxs(Row, {
                                    style: {
                                        width: "100%"
                                    },
                                    children: [
                                        /*#__PURE__*/ _jsx(Col, {
                                            style: {
                                                flex: 1
                                            },
                                            children: t("Translation status")
                                        }),
                                        /*#__PURE__*/ _jsx(Col, {
                                            children: isComplete && machineTranslationStatus === EMachineTranslationStatus.Full ? /*#__PURE__*/ _jsxs(Popover, {
                                                title: t("Machine translation"),
                                                content: t("This translation is fully machine translated."),
                                                children: [
                                                    /*#__PURE__*/ _jsx(RobotOutlined, {
                                                        style: {
                                                            marginRight: spacing.layout.content.marginSmall,
                                                            color: colors.translation.machineTranslated
                                                        }
                                                    }),
                                                    /*#__PURE__*/ _jsx("span", {
                                                        style: {
                                                            fontSize: "0.75em",
                                                            color: colors.translation.machineTranslated,
                                                            fontWeight: "normal"
                                                        },
                                                        children: t("Full")
                                                    })
                                                ]
                                            }) : machineTranslationStatus === EMachineTranslationStatus.Partly ? /*#__PURE__*/ _jsx(Popover, {
                                                title: t("Machine translation"),
                                                content: t("This translation is partly machine translated."),
                                                children: /*#__PURE__*/ _jsxs("div", {
                                                    style: {
                                                        display: "flex",
                                                        alignItems: "center"
                                                    },
                                                    children: [
                                                        /*#__PURE__*/ _jsx(RobotOutlined, {
                                                            style: {
                                                                marginRight: spacing.layout.content.marginSmall,
                                                                color: colors.translation.machineTranslated,
                                                                opacity: 0.6
                                                            }
                                                        }),
                                                        /*#__PURE__*/ _jsx("span", {
                                                            style: {
                                                                fontSize: "0.75em",
                                                                color: colors.translation.machineTranslated,
                                                                opacity: 0.8,
                                                                fontWeight: "normal"
                                                            },
                                                            children: t("Partly")
                                                        })
                                                    ]
                                                })
                                            }) : null
                                        })
                                    ]
                                }),
                                autoAdjustOverflow: true,
                                content: /*#__PURE__*/ _jsx(TranslationStatus, {
                                    defaultLanguageStatus: defaultLangStatus,
                                    targetStatus: foundInfo,
                                    language: lang,
                                    template: record,
                                    templateType: actions?.templateType,
                                    machineTranslationStatus: machineTranslationStatus
                                }),
                                trigger: "hover",
                                placement: "left",
                                children: /*#__PURE__*/ _jsx("div", {
                                    style: {
                                        opacity: !isComplete ? 0.5 : 1,
                                        lineHeight: 1
                                    },
                                    className: !isComplete && !hasAnyTranslation ? "crossClip" : "",
                                    children: getLanguageIcon(lang, {
                                        height: "1.2em",
                                        width: "1.2em"
                                    })
                                })
                            })
                        }, `popover-icon-${record.id}-${lang}}`);
                    })
                }, `${record.id}-languages-info}`);
            },
            onFilter: (value, record)=>{
                const [completeIndicator, language] = value.toString().split("-");
                const foundTranslation = record.translationInfo?.find((info)=>info.language === language);
                if ([
                    "1",
                    "2"
                ].includes(completeIndicator)) {
                    const isTranslationComplete = foundTranslation && (foundTranslation.isTranslationComplete || foundTranslation.isTranslationCompleteRecursive);
                    return completeIndicator === "1" === !!isTranslationComplete;
                }
                return !foundTranslation; // Handles case "3" for missing translations
            },
            filters: [
                {
                    text: t("Complete"),
                    value: "complete",
                    children: [
                        ...getSupportedLanguageOptions(t).filter((option)=>option.language !== DEFAULT_LANGUAGE && languages.includes(option.language)).map((option)=>{
                            return {
                                text: /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            style: {
                                                verticalAlign: "middle"
                                            },
                                            children: option.icon
                                        }),
                                        /*#__PURE__*/ _jsx("span", {
                                            style: {
                                                marginLeft: spacing.layout.content.marginMedium
                                            },
                                            children: option.label
                                        })
                                    ]
                                }),
                                value: `1-${option.language}`
                            };
                        })
                    ]
                },
                {
                    text: t("Incomplete"),
                    value: "incomplete",
                    children: [
                        ...getSupportedLanguageOptions(t).filter((option)=>option.language !== DEFAULT_LANGUAGE && languages.includes(option.language)).map((option)=>{
                            return {
                                text: /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            style: {
                                                verticalAlign: "middle"
                                            },
                                            children: option.icon
                                        }),
                                        /*#__PURE__*/ _jsx("span", {
                                            style: {
                                                marginLeft: spacing.layout.content.marginMedium
                                            },
                                            children: option.label
                                        })
                                    ]
                                }),
                                value: `2-${option.language}`
                            };
                        })
                    ]
                },
                {
                    text: t("Not yet translated"),
                    value: "missing",
                    children: [
                        ...getSupportedLanguageOptions(t).filter((option)=>option.language !== DEFAULT_LANGUAGE && languages.includes(option.language)).map((option)=>{
                            return {
                                text: /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            style: {
                                                verticalAlign: "middle"
                                            },
                                            children: option.icon
                                        }),
                                        /*#__PURE__*/ _jsx("span", {
                                            style: {
                                                marginLeft: spacing.layout.content.marginMedium
                                            },
                                            children: option.label
                                        })
                                    ]
                                }),
                                value: `3-${option.language}`
                            };
                        })
                    ]
                }
            ]
        },
        {
            title: t("Tier"),
            dataIndex: TemplateMetaDataField.Tier,
            key: TemplateMetaDataField.Tier,
            render: (_, record)=>{
                return /*#__PURE__*/ _jsx(Tag, {
                    color: record.tier === ERcbTier.Pro ? service.tier.pro.color : service.tier.free.color,
                    children: record.tier === ERcbTier.Pro ? "PRO" : "FREE"
                }, `${record.id}-${record.tier}`);
            },
            sorter: (a, b)=>{
                const aNum = a.tier === ERcbTier.Pro ? 1 : 0;
                const bNum = b.tier === ERcbTier.Pro ? 1 : 0;
                return aNum - bNum;
            },
            onFilter: (value, record)=>record.tier === value,
            filterSearch: true,
            filters: [
                {
                    text: t("Pro"),
                    value: ERcbTier.Pro
                },
                {
                    text: t("Free"),
                    value: ERcbTier.Free
                }
            ]
        },
        {
            title: t("Status"),
            dataIndex: TemplateMetaDataField.Status,
            key: TemplateMetaDataField.Status,
            render: (_, record)=>{
                return /*#__PURE__*/ _jsx(TemplateMetaDataStatusTag, {
                    toolTip: `${t("Version")} ${record.version}`,
                    status: record.status
                }, `${record.id}-${record.status}`);
            },
            sorter: (a, b)=>{
                const aNum = a.status === ETemplateStatus.Published ? 1 : 0;
                const bNum = b.status === ETemplateStatus.Published ? 1 : 0;
                return aNum - bNum;
            },
            onFilter: (value, record)=>record.tier === value,
            filterSearch: true,
            filters: [
                {
                    text: t("Published"),
                    value: ETemplateStatus.Published
                },
                {
                    text: t("Draft"),
                    value: ETemplateStatus.Draft
                }
            ]
        }
    ];
    if (actions.favouriteToggleType) {
        columns.unshift({
            title: /*#__PURE__*/ _jsx(StarTwoTone, {}),
            dataIndex: TemplateMetaDataField.IsFavourite,
            key: TemplateMetaDataField.IsFavourite,
            render: (_, record)=>/*#__PURE__*/ _jsx(TemplateFavouriteToggle, {
                    type: actions.favouriteToggleType,
                    template: record
                }, record.identifier),
            width: "3rem"
        });
    }
    return columns;
}
export { useTemplateMetaDataColumns };
